import { claimStore, fetchAiStoreData, fetchStore, fetchStoreList, fetchStoreListUrls } from '~/api/stores/index'
import {
  checkImportWarning,
  checkShippingTemplate,
  confirmImportWarning,
  fetchTiktokWarehouses,
  patchTiktokWarehouse,
} from '~/api/stores/tiktok'

const state = () => ({
  tiktokWarehouses: [],
  storeList: [],
  storeUrls: {},
  aiStoreData: null,
  claimStoreData: null,
  isConfirmImportWarning: false,
  isConfirmAvailableWarning: false,
  isTiktokShippingTemplateMissing: false,
  isActiveStoreFetched: false,
  needUpdateActiveStore: false,
})

const getters = {
  tiktokWarehouses: (state) => state.tiktokWarehouses,
  storeUrls: (state) => state.storeUrls,
  storeList: (state) => state.storeList,
  aiStoreData: (state) => state.aiStoreData,
  storesCount: (state) => Object.keys(state.storeUrls).length,
  claimStoreData: (state) => state.claimStoreData,
  isConfirmImportWarning: (state) => state.isConfirmImportWarning,
  isConfirmAvailableWarning: (state) => state.isConfirmAvailableWarning,
  isTiktokShippingTemplateMissing: (state) => state.isTiktokShippingTemplateMissing,
  isActiveStoreFetched: (state) => state.isActiveStoreFetched,
  needUpdateActiveStore: (state) => state.needUpdateActiveStore,
}

const mutations = {
  setNeedUpdateActiveStore(state, value) {
    state.needUpdateActiveStore = value
  },

  setIsActiveStoreFetched(state, value) {
    state.isActiveStoreFetched = value
  },

  setStoreUrls(state, data) {
    state.storeUrls = data
  },
  setStoreList(state, stores = []) {
    state.storeList = stores
    const previousStoreIds = localStorage.getItem('storeList') ? JSON.parse(localStorage.getItem('storeList')) : []
    const currentStoreIds = stores.map(store => store.id)

    const toSortedKey = (ids) => [...ids].sort((a, b) => a - b).join(',')
    if (toSortedKey(previousStoreIds) !== toSortedKey(currentStoreIds)) {
      localStorage.setItem('storeList', JSON.stringify(currentStoreIds))
      const cachedActiveStoreId = Number(localStorage.getItem('activeStore'))
      if (!currentStoreIds.includes(cachedActiveStoreId)) {
        localStorage.removeItem('activeStore')
      }
    }
  },
  setTiktokWarehouses(state, warehouses = []) {
    state.tiktokWarehouses = warehouses
  },
  setAiStoreData(state, data) {
    state.aiStoreData = data
  },
  setTiktokShippingTemplateState(state, data) {
    state.isTiktokShippingTemplateMissing = data
  },
  setClaimStoreData(state, data) {
    state.claimStoreData = data
  },
  setConfirmImportWarning(state, value) {
    state.isConfirmImportWarning = value
  },
  setConfirmAvailableWarning(state, value) {
    state.isConfirmAvailableWarning = value
  },
}

const actions = {
  async fetchStoreList({ commit }) {
    try {
      const storeList = await fetchStoreList()
      commit('setStoreList', storeList)
    } catch (e) {
      console.error('error in stores/fetchStoreList')
      throw e
    }
  },
  async fetchStore({ commit, rootGetters }, preloadedRequest) {
    try {
      const expectedId = rootGetters['auth/activeStore']?.id
      const preloaded = await preloadedRequest?.catch(() => null)
      const storeData = preloaded && preloaded.id === expectedId
        ? preloaded
        : await fetchStore()
      commit('setTiktokShippingTemplateState', storeData.tiktok_shipping_template_missing)
      commit('auth/setActiveStore', storeData, { root: true })
      commit('setIsActiveStoreFetched', true)
    } catch (e) {
      console.error('error in stores/fetchStoreData')
      throw e
    }
  },
  async fetchStoreListUrls({ commit }) {
    try {
      const storeUrls = await fetchStoreListUrls()
      const preparedUrls = storeUrls.reduce((acc, store) => {
        acc[store.storeType] = store.authLink
        return acc
      }, {})

      commit('setStoreUrls', preparedUrls)
    } catch (e) {
      console.error('error in stores/fetchStoreListUrls')
      console.error(e)
    }
  },

  async claimStore({ commit }) {
    try {
      const { data } = await claimStore()
      commit('setClaimStoreData', data.data)
    } catch (e) {
      console.error('error in stores/claimStore')
      throw e
    }
  },

  async fetchAiStoreData({ commit }) {
    try {
      const storeData = await fetchAiStoreData()
      commit('setAiStoreData', storeData[0]?.context)
    } catch (e) {
      console.error('error in stores/fetchAiStoreData')
      console.error(e)
    }
  },

  async fetchTiktokWarehouses({ commit }) {
    try {
      const tiktokWarehouses = await fetchTiktokWarehouses()
      commit('setTiktokWarehouses', tiktokWarehouses)
    } catch (e) {
      console.error('error in stores/fetchTiktokWarehouses')
      console.error(e)
    }
  },

  async patchTiktokWarehouse(_, data) {
    try {
      await patchTiktokWarehouse(data)
    } catch (e) {
      console.error('error in stores/patchTiktokWarehouse')
      console.error(e)
    }
  },

  async checkShippingTemplate() {
    try {
      await checkShippingTemplate()
    } catch (e) {
      console.error('error in stores/checkShippingTemplate', e)
      throw e
    }
  },

  async confirmWarning({ commit }, key) {
    try {
      await confirmImportWarning(key)
      const map = {
        importWarning: () => commit('setConfirmImportWarning', true),
        availableWarning: () => commit('setConfirmAvailableWarning', true),
      }

      map[key]()
    } catch (e) {
      console.error('error in stores/confirmImportWarning')
      throw e
    }
  },

  async checkImportWarning({ commit }) {
    try {
      const result = await checkImportWarning()
      const map = result.reduce((acc, item) => {
        acc[item.key] = item.context.value
        return acc
      }, {})
      commit('setConfirmImportWarning', map.importWarning)
      commit('setConfirmAvailableWarning', map.availableWarning)
    } catch (e) {
      console.error('error in stores/checkImportWarning')
      throw e
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
